import * as React from "react"

import { HugeiconsIcon, Icons } from "@/components/icons"

import { cn } from "@/lib/utils"
import { useIntl } from "react-intl"

const emptyStateVariants = {
  default: "bg-transparent",
  bordered: "border-border bg-secondary-background rounded-lg border",
  card: "border-border bg-card rounded-lg border shadow-sm",
} as const

export interface EmptyStateClassNames {
  root?: string
  icon?: string
  title?: string
  description?: string
  action?: string
}

interface EmptyStateProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  /** Visual variant */
  variant?: keyof typeof emptyStateVariants
  /** Icon to display (default: File01) */
  icon?: React.ReactNode
  /** Title text. Default: common.emptyTitle */
  title?: React.ReactNode
  /** Description text. Default: common.emptyDescription */
  description?: React.ReactNode
  /** Action button/link */
  action?: React.ReactNode
  /** Size of the container padding */
  size?: "sm" | "md" | "lg"
  /** Show title. Default true. Set false to hide. */
  showTitle?: boolean
  /** Show description. Default true. Set false to hide. */
  showDescription?: boolean
  /** Class names for internal parts */
  classNames?: EmptyStateClassNames
}

const sizeClasses = {
  sm: "p-2",
  md: "p-4",
  lg: "p-6",
} as const

const iconSizes = {
  sm: 40,
  md: 48,
  lg: 64,
} as const

function EmptyState({
  className,
  variant = "default",
  icon,
  title,
  description,
  action,
  size = "md",
  showTitle = true,
  showDescription = true,
  classNames,
  children,
  ...props
}: EmptyStateProps) {
  const intl = useIntl()
  const displayTitle = title ?? intl.formatMessage({ id: "common.emptyTitle" })
  const displayDescription =
    description ?? intl.formatMessage({ id: "common.emptyDescription" })

  const defaultIcon = (
    <HugeiconsIcon
      icon={Icons.FileNotFound}
      size={iconSizes[size]}
      className="text-alternate"
      aria-hidden
    />
  )

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 text-center",
        emptyStateVariants[variant],
        sizeClasses[size],
        classNames?.root,
        className
      )}
      {...props}
    >
      {children ?? (
        <>
          <div
            className={cn(
              "text-alternate flex items-center justify-center",
              classNames?.icon
            )}
          >
            {icon ?? defaultIcon}
          </div>
          <div className="space-y-4">
            {showTitle && (
              <h3
                className={cn(
                  "app-text-title-small text-secondary-text",
                  classNames?.title
                )}
              >
                {displayTitle}
              </h3>
            )}
            {showDescription && (
              <p
                className={cn(
                  "app-text-body-small text-secondary-text",
                  classNames?.description
                )}
              >
                {displayDescription}
              </p>
            )}
          </div>
          {action && (
            <div className={cn("mt-2", classNames?.action)}>{action}</div>
          )}
        </>
      )}
    </div>
  )
}

EmptyState.displayName = "EmptyState"

export { EmptyState, emptyStateVariants }
